<template>
  <div class="additional-metaproperties">
    <div
      v-if="isLoading"
      class="text-center"
    >
      <b-spinner variant="primary" />
    </div>
    <div
      v-else-if="!hasError && (isRegularPublish || currentPlatformId)"
      class="border rounded"
    >
      <div
        class="d-flex justify-content-between align-items-center p-2 cursor-pointer"
        @click="isCollapsed = !isCollapsed"
      >
        <h6 class="mb-0">
          Image Metadata
        </h6>
        <i :class="['fas', isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']" />
      </div>
      <div
        v-if="!isCollapsed"
        :class="!isRegularPublish ? 'metaproperties-content' : ''"
      >
        <!-- Longtext -->
        <div
          v-if="inputProps.length"
          class="row m-2"
        >
          <div
            v-for="prop in inputProps"
            :key="prop.id"
            class="col-md"
          >
            <div class="mb-3">
              <label>{{ prop.label }}</label>
              <input
                v-model="selectedValues[prop.id]"
                type="text"
                class="form-control"
                @input="updateInputValue($event, prop.id)"
              >
            </div>
          </div>
        </div>

        <!-- Date -->
        <div
          v-if="dateProps.length"
          class="row m-2"
        >
          <div
            v-for="prop in dateProps"
            :key="prop.id"
            class="col-md-6"
          >
            <div class="mb-3">
              <label>{{ prop.label }}</label>
              <input
                v-model="selectedValues[prop.id]"
                type="date"
                class="form-control"
                @input="updateDateValue($event, prop.id)"
              >
            </div>
          </div>
        </div>

        <!-- Multiselect -->
        <div
          v-if="multiselectProps.length"
          class="row m-2"
        >
          <div
            v-for="prop in multiselectProps"
            :key="prop.id"
            class="col-md-6"
          >
            <div class="mb-3">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <label class="mb-0">{{ prop.label }}</label>
                <img
                  v-if="prop.name === bynder.PEPSI_GTIN && selectedValues[prop.id] && selectedValues[prop.id].length"
                  class="copy-icon"
                  src="/img/icons/copy-icon.png"
                  alt="Copy"
                  data-toggle="tooltip"
                  data-placement="top"
                  title="Copy GTIN"
                  @click="copyToClipboard(selectedValues[prop.id])"
                >
              </div>
              <Multiselect
                v-model="selectedValues[prop.id]"
                :options="prop.options"
                :multiple="true"
                :taggable="true"
                track-by="id"
                label="label"
                :placeholder="prop.label"
                @input="updateMultiSelectValue($event, prop.id)"
                @tag="addNewTag($event, prop.id)"
              />
            </div>
          </div>
        </div>

        <!-- Select -->
        <div
          v-if="selectProps.length"
          class="row m-2"
        >
          <div
            v-for="prop in selectProps"
            :key="prop.id"
            class="col-md-6"
          >
            <div class="mb-3">
              <label>{{ prop.label }}</label>
              <Multiselect
                v-model="selectedValues[prop.id]"
                :options="prop.options"
                :multiple="false"
                :taggable="true"
                track-by="id"
                label="label"
                :placeholder="prop.label"
                @input="updateSelectValue($event, prop.id)"
                @tag="addNewSelectTag($event, prop.id)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import {
  BYNDER_EVENTS,
  bynderEventBroker,
} from '@frontend/group/modules/bynder/event-broker';
import { mapGetters, mapActions } from 'vuex';
import * as bynder from '@frontend/constants/bynder.js';

const metapropertiesCache = {};
const metapropertiesInFlight = {};

async function fetchItemMetaproperties(platformId) {
  if (!metapropertiesCache[platformId]) {
    if (!metapropertiesInFlight[platformId]) {
      metapropertiesInFlight[platformId] = axios.post('/bynder-metadata/fetch-metaproperties', {
        platform_id: platformId,
        is_item_specific: true,
      });
    }

    try {
      const { data } = await metapropertiesInFlight[platformId];

      if (data.status === 'error') {
        throw new Error(data.message);
      }

      metapropertiesCache[platformId] = data.metaproperties;
    } finally {
      delete metapropertiesInFlight[platformId];
    }
  }

  const metaproperties = metapropertiesCache[platformId];

  const flattened = [
    ...(metaproperties.longtext || []),
    ...(metaproperties.date || []),
    ...(metaproperties.select || []),
    ...(metaproperties.multiselect || []),
  ];

  return JSON.parse(JSON.stringify(flattened));
}

export default {
  name: "AdditionalMetaproperties",
  components: { Multiselect },

  props: {
    isRegularPublish: {
      type: Boolean,
      default: true,
    },
    isAiPublish: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    bynder,
    instanceId: null,
    previewData: null,
    props: [],
    selectedValues: {},
    dateOrder: [
      bynder.PEPSI_IN_MARKET_DATE,
      bynder.PEPSI_EXPIRY_DATE,
      bynder.RAPID_IN_MARKET_DATE,
      bynder.RAPID_EXPIRY_DATE,
    ],
    multiselectOrder: [
      bynder.PEPSI_GTIN,
      bynder.PEPSI_GVN,
      bynder.PEPSI_BRAND,
      bynder.PEPSI_SUB_BRAND,
      bynder.PEPSI_TARGET_MARKET,
    ],
    selectOrder: [
      bynder.PEPSI_IMAGE_NATURE,
      bynder.PEPSI_FACING_INDICATOR,
      bynder.PEPSI_ANGLE_IDENTIFIER,
      bynder.PEPSI_IO_PACKAGING,
      bynder.PEPSI_SEQUENCE_NUMBER,
    ],
    isLoading: true,
    hasError: false,
    // fileName: '00012000170577_C1N1_s21.jpg', // for testing
    fileName: null,
    imageNature: '',
    facingIndicator: '',
    angleIdentifier: '',
    ioPackaging: '',
    sequenceNumber: null,
    language: '',
    languageCode: null,
    fileNameMetaMap: {
      [bynder.PEPSI_IMAGE_NATURE]: '',
      [bynder.PEPSI_FACING_INDICATOR]: '',
      [bynder.PEPSI_ANGLE_IDENTIFIER]: '',
      [bynder.PEPSI_IO_PACKAGING]: '',
      [bynder.PEPSI_SEQUENCE_NUMBER]: '',
    },
    templateName: null,
    GTIN: null,
    isCollapsed: true,
    fileNameParts: null,
    debounceTimer: null,
    sector: null,
    currentPlatformId: null,
    originalName: null
  }),

  computed: {
    dateProps() {
      const priorityProps = this.dateOrder
        .map(orderName => this.props.find(prop => prop.alt_type === 'date' && prop.name === orderName))
        .filter(Boolean);

      const otherProps = this.props.filter(
        prop => prop.alt_type === 'date' && !this.dateOrder.includes(prop.name)
      );

      return [...priorityProps, ...otherProps];
    },
    multiselectProps() {
      const priorityProps = this.multiselectOrder
        .map(orderName => this.props.find(prop => prop.alt_type === 'multiselect' && prop.name === orderName))
        .filter(Boolean);

      const otherProps = this.props.filter(
        prop => prop.alt_type === 'multiselect' && !this.multiselectOrder.includes(prop.name)
      );

      return [...priorityProps, ...otherProps];
    },
    selectProps() {
      const priorityProps = this.selectOrder
        .map(orderName => this.props.find(prop => prop.alt_type === 'select' && prop.name === orderName))
        .filter(Boolean);

      const otherProps = this.props.filter(
        prop => prop.alt_type === 'select' && !this.selectOrder.includes(prop.name)
      );

      return [...priorityProps, ...otherProps];
    },
    inputProps() {
      return this.props.filter(prop => prop.alt_type === 'longtext');
    },
  },

  mounted() {
    if (this.isRegularPublish) {
      this.instanceId = $(this.$el).closest('[data-instance-id]').data('instance-id');
      bynderEventBroker.on(BYNDER_EVENTS.HANDLE_ADDITIONAL_PROPERTIES, this.onHandleAdditionalProperties);
    } else {
      this.instanceId = 'ai-render';
      this.isLoading = false;
    }

    bynderEventBroker.on(BYNDER_EVENTS.HANDLE_PUBLISH_ADD_PROPS, this.onHandlePublishAddProps);
  },

  beforeDestroy() {
    bynderEventBroker.off(BYNDER_EVENTS.HANDLE_ADDITIONAL_PROPERTIES, this.onHandleAdditionalProperties);
    bynderEventBroker.off(BYNDER_EVENTS.HANDLE_PUBLISH_ADD_PROPS, this.onHandlePublishAddProps);
  },

  methods: {
    ...mapActions('bynder', [
      'addAdditionalProperty',
      'updateAdditionalProperty',
      'updateFilename',
      'clearAdditionalPropertiesById',
    ]),

    onHandleAdditionalProperties({ instanceId, previews }) {
      if (instanceId === this.instanceId) {
        this.init(previews);
      }
    },

    onHandlePublishAddProps({ platformId, imageMeta }) {
      this.previewData = imageMeta;
      this.originalName = this.previewData?.original_name ?? null;

      this.currentPlatformId = platformId;
      this.isLoading = true;
      this.init([]);
    },

    async init(previews = []) {
      if (this.isRegularPublish && previews.length) {
        this.clearAdditionalPropertiesById({ instanceId: this.instanceId });

        this.isCollapsed = previews[0].id === this.instanceId ? false : true;

        this.previewData = previews.find(preview => preview.id === this.instanceId)?.data;
      }

      this.fileName = this.previewData?.file_name ?? null;
      this.templateName = this.previewData?.template_name ?? null;
      this.GTIN = this.previewData?.gtin ?? null;
      this.sector = this.previewData?.sector ?? null;

      if (!this.isRegularPublish && this.isAiPublish) {
        this.imageNature = 'A';
        this.facingIndicator = 'H';
        this.angleIdentifier = '1';
        this.ioPackaging = 'C';
        this.sequenceNumber = '1';
        this.fileNameMetaMap[bynder.PEPSI_IMAGE_NATURE] = 'A';
        this.fileNameMetaMap[bynder.PEPSI_FACING_INDICATOR] = 'H';
        this.fileNameMetaMap[bynder.PEPSI_ANGLE_IDENTIFIER] = '1';
        this.fileNameMetaMap[bynder.PEPSI_IO_PACKAGING] = 'C';
        this.fileNameMetaMap[bynder.PEPSI_SEQUENCE_NUMBER] = '1';
      } else {
        this.parseFilename();
      }

      try {
        this.props = await this.fetchMetaproperties();
        this.hasError = false;
      } catch (error) {
        this.hasError = true;
        this.isLoading = false;
        return;
      }

      this.isLoading = false;

      const batchedUpdates = {};

      this.props.forEach(prop => {
        if (!prop.name) {
          console.log('Invalid property', prop);
          return;
        }

        let value;
        if (prop.alt_type === 'date') {
          value = this.getDateValue(prop);
        } else if (prop.alt_type === 'multiselect') {
          value = this.getMultiselectDefaultValue(prop);
        } else if (prop.alt_type === 'select') {
          value = this.getSelectDefaultValue(prop);
        } else if (prop.alt_type === 'longtext') {
          value = this.getInputDefaultValue(prop);
        }

        batchedUpdates[prop.id] = value;
      });

      this.selectedValues = { ...batchedUpdates };

      this.updateStore(this.selectedValues);

      if (!this.isRegularPublish && this.isAiPublish) {
        this.buildAiFileName();
      }

      if (this.isRegularPublish && !this.isAiPublish) {
        this.buildUpdatedFileName();
      }
    },

    async fetchMetaproperties() {
      const platformId = this.currentPlatformId || $('#deployment_platform').find(':selected').val();

      return fetchItemMetaproperties(platformId);
    },

    getMultiselectDefaultValue(prop) {
      let value = this.previewData?.[prop.default_value] ?? null;
      let defaultValue = prop.default_value ?? null;

      if (!value && !defaultValue) {
        return prop.alt_type === 'multiselect' ? [] : null;
      }

      const options = prop.options || [];

      // If the sub brand is not set, set it to the brand
      if (prop.name === bynder.PEPSI_SUB_BRAND && !value) {
        value = this.previewData?.brand ?? null;
      }

      if (prop.name === bynder.PEPSI_SECTOR && this.sector) {
        value = bynder.PEPSI_SECTOR_MAP[this.sector] ?? defaultValue ?? null;
      }

      if (prop.name === bynder.PEPSI_LANGUAGE) {
        value = this.language ? this.language : prop.default_value ?? null;
      }

      const optionMap = new Map();
      options.forEach(option => {
        optionMap.set(option.id, option);
        optionMap.set(option.name, option);
        optionMap.set(option.label, option);
      });

      let matchingOption = optionMap.get(value);

      if (!matchingOption) {
        if (this.multiselectOrder.includes(prop.name) && !value) {
          return [];
        }

        matchingOption = optionMap.get(defaultValue);
      }

      if (!matchingOption) {
        matchingOption = {
          id: value,
          label: value,
          name: value,
        };
        options.push(matchingOption);
      }

      return prop.alt_type === 'multiselect' ? [matchingOption] : matchingOption;
    },

    getSelectDefaultValue(prop) {
      let value = this.fileNameMetaMap[prop.name] ? this.fileNameMetaMap[prop.name] : prop.default_value ?? '';

      if (prop.name === bynder.PEPSI_TARGET_CUSTOMER) {
        value = (this.templateName && this.templateName.includes('Kroger'))
          ? prop.default_value
          : '';
      }

      const optionMap = new Map();
      prop.options.forEach(option => {
        optionMap.set(option.id, option);
        optionMap.set(option.name, option);
        optionMap.set(option.label, option);
      });

      let matchingOption = optionMap.get(value);

      if (prop.name === bynder.PEPSI_IMAGE_NATURE && matchingOption) {
        this.imageNature = matchingOption.name;
      }

      if (!matchingOption) {
        matchingOption = {
          id: value,
          label: value,
          name: value,
        };
        prop.options.push(matchingOption);
      }

      return matchingOption;
    },

    getDateValue(prop) {
      if ((prop.name === bynder.PEPSI_EXPIRY_DATE || prop.name === bynder.RAPID_EXPIRY_DATE) && !this.previewData?.[prop.default_value]) {

        return bynder.DEFAULT_EXPIRY_DATE;
      }

      return this.formatDate(this.previewData?.[prop.default_value] ?? '');
    },

    getInputDefaultValue(prop) {
      return this.previewData?.[prop.default_value] ?? '';
    },

    formatDate(dateString) {
      if (dateString) {
        try {
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        } catch (e) {
          console.error('Invalid date format:', dateString);
          return '';
        }
      }
      return '';
    },

    updateInputValue(event, propId) {
      const value = event.target.value;
      this.$set(this.selectedValues, propId, value);

      this.debouncedUpdateStore(propId, value, value);
    },

    updateDateValue(event, propId) {
      const value = event.target.value;
      this.$set(this.selectedValues, propId, value);
      this.updateStoreForProperty(propId, value, value);
    },

    updateMultiSelectValue(selectedItems, propId) {
      const validItems = Array.isArray(selectedItems) ? selectedItems : [];
      this.$set(this.selectedValues, propId, validItems);

      const vuexValue = validItems.map(item => item.id).join(',');
      const propValue = validItems.map(item => item.label).join(', ');
      this.updateStoreForProperty(propId, vuexValue, propValue);
    },

    addNewTag(newTag, propId) {
      const newOption = {
        id: newTag,
        label: newTag,
        name: newTag,
      };

      const prop = this.props.find(p => p.id === propId);
      if (prop && Array.isArray(prop.options)) {
        prop.options.push(newOption);
      }

      const currentValues = this.selectedValues[propId] || [];
      const updatedValues = [...currentValues, newOption];
      this.$set(this.selectedValues, propId, updatedValues);

      const vuexValue = updatedValues.map(item => item.id || item.label).join(',');
      const propValue = updatedValues.map(item => item.label).join(', ');
      this.updateStoreForProperty(propId, vuexValue, propValue);
      this.updateNameSection(propId);
    },

    updateStore(newSelectedOptions) {
      const formData = {};

      const propsLength = this.props.length;

      for (let i = 0; i < propsLength; i++) {
        const prop = this.props[i];
        let value = newSelectedOptions[prop.id] || '';
        let propValue = '';

        if (prop.alt_type === 'multiselect') {
          if (!Array.isArray(value)) {
            value = [];
          }

          value = value.map(item => item?.id).filter(id => id).join(',');
          propValue = newSelectedOptions[prop.id] ? newSelectedOptions[prop.id].map(item => item.label).join(', ') : '';
        } else if (prop.alt_type === 'select') {
          propValue = value?.label || '';
          value = value?.id || '';
        } else if (prop.alt_type === 'date' && !value) {
          value = this.getDateValue(prop);
          propValue = value;
        } else {
          propValue = value;
        }

        formData[prop.id] = {
          value: value,
          name: prop.name,
          prop_value: propValue,
        };
      }

      this.addAdditionalProperty({
        instanceId: this.instanceId,
        data: formData,
      });
    },

    updateStoreForProperty(propId, value, propValue) {
      const prop = this.props.find(p => p.id === propId);
      if (!prop) return;

      let storeValue = value;
      let storePropValue = propValue;

      if (prop.alt_type === 'multiselect' && typeof value === 'string') {
        const ids = value.split(',');
        const selectedItems = ids.map(id =>
          Object.values(prop.options ?? {}).find(
            option => option.id === id
          )
        ).filter(Boolean);
        this.$set(this.selectedValues, propId, selectedItems);
        storePropValue = selectedItems.map(item => item.label).join(', ');
      } else if (prop.alt_type === 'select') {
        storeValue = value?.id || value;
        storePropValue = propValue || (value?.label || '');
      }

      this.updateAdditionalProperty({
        instanceId: this.instanceId,
        propId,
        value: storeValue,
        name: prop.name,
        prop_value: storePropValue,
      });
    },

    updateSelectValue(selectedItem, propId) {
      const value = selectedItem ? selectedItem.id : '';
      const propValue = selectedItem ? selectedItem.label : '';
      this.$set(this.selectedValues, propId, selectedItem);
      this.updateStoreForProperty(propId, value, propValue);

      this.updateNameSection(propId, selectedItem?.name);
    },

    addNewSelectTag(newTag, propId) {
      const newOption = {
        id: newTag,
        label: newTag,
        name: newTag,
      };

      const prop = this.props.find(p => p.id === propId);
      if (prop && Array.isArray(prop.options)) {
        prop.options.push(newOption);
      }

      this.$set(this.selectedValues, propId, newOption);
      this.updateStoreForProperty(propId, newOption.id, newOption.label);
    },

    updateNameSection(propId, value) {
      const prop = this.props.find(p => p.id === propId);

      switch (prop.name) {
        case bynder.PEPSI_IMAGE_NATURE:
          this.imageNature = value;
          break;
        case bynder.PEPSI_FACING_INDICATOR:
          this.facingIndicator = value;
          break;
        case bynder.PEPSI_ANGLE_IDENTIFIER:
          this.angleIdentifier = value;
          break;
        case bynder.PEPSI_IO_PACKAGING:
          this.ioPackaging = value;
          break;
        case bynder.PEPSI_SEQUENCE_NUMBER:
          this.sequenceNumber = value;
          break;
      }

      if (!this.isRegularPublish && this.isAiPublish) {
        this.buildAiFileName();
      }

      if (this.isRegularPublish && !this.isAiPublish) {
        this.buildUpdatedFileName();
      }
    },

    parseFilename() {
      if (!this.fileName) {
        console.log('File Name: There is no file name');
        return;
      }

      const parts = this.fileName.split('.');
      this.fileNameParts = parts;

      const nameWithoutExtension = parts[0];
      const segments = nameWithoutExtension.split('_');

      if (segments.length < 2) {
        console.log('File Name: Invalid filename format');
        return;
      }

      const metadataSection = segments[1];
      if (metadataSection.length < 4) {
        console.log('File Name: Invalid metadata format');
        return;
      }

      // this.imageNature = metadataSection[0];
      this.facingIndicator = metadataSection[1];
      this.angleIdentifier = metadataSection[2];
      this.ioPackaging = metadataSection[3];

      if (segments.length > 2) {
        const trailingSegments = segments.slice(2);

        for (let i = 0; i < trailingSegments.length; i++) {
          const segment = trailingSegments[i];

          if (segment.startsWith('s') && /^s\d+$/.test(segment)) {
            this.sequenceNumber = segment;

            trailingSegments.splice(i, 1);
            break;
          }
        }

        for (let i = 0; i < trailingSegments.length; i++) {
          const segment = trailingSegments[i];

          this.languageCode = Object.keys(bynder.LANGUAGE_MAP).find(code => code === segment);

          if (this.languageCode) {
            this.language = bynder.LANGUAGE_MAP[this.languageCode];
            break;
          }
        }
      }

      // this.fileNameMetaMap[bynder.PEPSI_IMAGE_NATURE] = this.imageNature;
      this.fileNameMetaMap[bynder.PEPSI_FACING_INDICATOR] = this.facingIndicator;
      this.fileNameMetaMap[bynder.PEPSI_ANGLE_IDENTIFIER] = this.angleIdentifier;
      this.fileNameMetaMap[bynder.PEPSI_IO_PACKAGING] = this.ioPackaging;
      this.fileNameMetaMap[bynder.PEPSI_SEQUENCE_NUMBER] = this.sequenceNumber;
    },

    buildUpdatedFileName() {
      if (!this.fileName || !this.fileNameParts) {
        return;
      }

      const nameWithoutExtension = this.fileNameParts[0];
      const extension = this.fileNameParts.length > 1 ? this.fileNameParts[this.fileNameParts.length - 1] : 'jpg';
      const segments = nameWithoutExtension.split('_');

      if (segments.length < 1) {
        console.log('File Name: Invalid filename format');
        return;
      }

      const basePart = segments[0];

      let metadataSection = '';
      if (this.imageNature && this.facingIndicator && this.angleIdentifier && this.ioPackaging) {
        metadataSection = this.imageNature + this.facingIndicator + this.angleIdentifier + this.ioPackaging;
      } else {
        const originalMetadataSection = segments.length > 1 ? segments[1] : '';
        if (originalMetadataSection.length >= 4) {
          metadataSection =
            (this.imageNature || originalMetadataSection[0]) +
            (this.facingIndicator || originalMetadataSection[1]) +
            (this.angleIdentifier || originalMetadataSection[2]) +
            (this.ioPackaging || originalMetadataSection[3]);
        }
      }

      let newFileName = basePart + '_' + metadataSection;

      newFileName += this.languageCode ? '_' + this.languageCode : '';
      newFileName += this.sequenceNumber ? '_' + this.sequenceNumber : '';
      newFileName += '.' + extension;

      this.updateFilename({
        instanceId: this.instanceId,
        fileName: newFileName
      });

      return newFileName;
    },

    buildAiFileName() {
      if (!this.originalName) {
        return;
      }

      const nameWithoutExtension = this.originalName.replace(/\.[^.]+$/, '');
      const imageNature = this.imageNature || 'A';
      const facingIndicator = this.facingIndicator || 'H';
      const angleIdentifier = this.angleIdentifier || '1';
      const ioPackaging = this.ioPackaging || 'C';
      const sequenceNumber = this.sequenceNumber || '1';

      const newFileName = `${nameWithoutExtension}_${imageNature}_${facingIndicator}${angleIdentifier}${ioPackaging}${sequenceNumber}`;

      this.updateFilename({
        instanceId: this.instanceId,
        fileName: newFileName
      });

      return newFileName;
    },

    debouncedUpdateStore(propId, value, propValue) {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = setTimeout(() => {
        this.updateStoreForProperty(propId, value, propValue);
      }, 300);
    },

    copyToClipboard(items) {
      const text = items.map(item => item.name).join(',');

      navigator.clipboard.writeText(text);
      toastr.success(`Copied GTIN value: ${text}`);
    },
  },
};
</script>

<style scoped>
.metaproperties-content {
  min-height: 250px;
  max-height: 500px;
  overflow-y: auto;
}

.copy-icon {
  cursor: pointer;
  height: 20px;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.copy-icon:hover {
  transform: scale(1.1);
  opacity: 0.8;
}
</style>
